<template>
  <div class="analytics-tab">
    <recognition-filters
      :programs="programs"
      :program-id="programId"
      :selected-program="selectedProgram"
      :selected-date-range="selectedDateRange"
      :date-range-options="RECOGNITION_ANALYTICS_DATE_RANGES"
      :has-active-filters="hasActiveFilters"
      @update:selected-program="selectedProgram = $event"
      @update:selected-date-range="selectedDateRange = $event"
      @reset="resetFilters"
    >
      <template #prepend>
        <b-form-select
          v-if="hasDivisions"
          v-model="selectedDivision"
          :options="divisionOptions"
          class="recognition-filters__filter"
        />
      </template>
    </recognition-filters>

    <b-alert v-if="error && !loading" variant="danger" show>
      Unable to load analytics data. Please try adjusting your filters or try again later.
    </b-alert>
    <template v-else>
      <!-- Reward Spend -->
      <reward-spend-section :summary="summary" :spend-over-time="spendOverTime" :loading="loading" />

      <!-- Reward Redemption -->
      <reward-redemption-section :redemption="redemption" />

      <!-- Employee Engagement -->
      <employee-engagement-section :engagement="engagement" />

      <!-- Top Employees Tables -->
      <b-row class="analytics-tab__tables-row">
        <b-col cols="12" lg="7">
          <top-employees-table :employees="topEmployees" :loading="loading" />
        </b-col>
        <b-col cols="12" lg="5">
          <top-recognisers-table :recognisers="topRecognisers" :loading="loading" />
        </b-col>
      </b-row>

      <!-- Qualities Recognised -->
      <qualities-section :qualities="qualities" :loading="loading" />
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import RecognitionFilters from '@/pages/recognition/components/RecognitionFilters.vue';
import RewardSpendSection from '@/pages/recognition/components/analytics/RewardSpendSection.vue';
import RewardRedemptionSection from '@/pages/recognition/components/analytics/RewardRedemptionSection.vue';
import EmployeeEngagementSection from '@/pages/recognition/components/analytics/EmployeeEngagementSection.vue';
import TopEmployeesTable from '@/pages/recognition/components/analytics/TopEmployeesTable.vue';
import TopRecognisersTable from '@/pages/recognition/components/analytics/TopRecognisersTable.vue';
import QualitiesSection from '@/pages/recognition/components/analytics/QualitiesSection.vue';
import { RECOGNITION_ANALYTICS_DATE_RANGES, getDateFromForRange } from '@/pages/recognition/constants';
import {
  transformSummary,
  transformSpendOverTime,
  transformRedemptionBreakdown,
  transformEngagement,
  transformTopEmployees,
  transformTopRecognisers,
  transformQualities,
} from '@/pages/recognition/utils/analyticsTransformers';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import ApiClient from '@/ApiClient';

const props = defineProps({
  programs: {
    type: Array,
    default: () => [],
  },
  programId: {
    type: String,
    default: null,
  },
});

const { divisions, hasDivisions } = useDivisionSelection();

const selectedDivision = ref(null);
const selectedProgram = ref(null);
const selectedDateRange = ref('last_12_months');

const activeProgramId = computed(() => props.programId || selectedProgram.value);

const summary = ref(undefined);
const spendOverTime = ref(null);
const redemption = ref(undefined);
const engagement = ref(undefined);
const topEmployees = ref([]);
const topRecognisers = ref([]);
const qualities = ref(undefined);
const loading = ref(false);
const error = ref(false);
const fetchRequestId = ref(0);

const divisionOptions = computed(() => {
  const options = [{ value: null, text: 'Entire company' }];
  for (const division of divisions.value) {
    options.push({
      value: division.division_id,
      text: division.name,
    });
  }
  return options;
});

const hasActiveFilters = computed(() => {
  const hasDivisionFilter = selectedDivision.value !== null;
  const hasProgramFilter = !props.programId && selectedProgram.value !== null;
  const hasDateFilter = selectedDateRange.value !== 'last_12_months';
  return hasDivisionFilter || hasProgramFilter || hasDateFilter;
});

const buildParams = () => {
  const params = {};
  if (selectedDivision.value) {
    params.division_ids = [selectedDivision.value];
  }
  const startDate = getDateFromForRange(selectedDateRange.value);
  if (startDate) {
    params.start_date = startDate;
  }
  const now = new Date();
  params.end_date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
  return params;
};

const aggregateResults = results => {
  const summaryAgg = { total_granted_minor: 0, total_redeemed_minor: 0, awards_granted: 0, user_participation: 0 };
  const redemptionTotals = { cash: 0, savings: 0, gift_cards: 0 };
  const divisionMap = new Map();
  let labels = [];

  // Engagement aggregation
  let engagementCount = 0;
  const engagementAgg = {
    recognition_sent: 0,
    recognition_sent_change_pct: 0,
    recognition_sent_trend: [],
    nominations_received: 0,
    nominations_received_change_pct: 0,
    nominations_received_trend: [],
    employee_participation: 0,
    employee_participation_change_pct: 0,
    employee_participation_trend: [],
  };

  // Top employees/recognisers - merge and re-sort
  const employeeMap = new Map();
  const recogniserMap = new Map();

  // Qualities aggregation
  const qualityMap = new Map();

  for (const [s, spend, r, eng, topEmp, topRec, qual] of results) {
    // Summary aggregation (existing)
    summaryAgg.total_granted_minor += s?.total_granted_minor ?? 0;
    summaryAgg.total_redeemed_minor += s?.total_redeemed_minor ?? 0;
    summaryAgg.awards_granted += s?.awards_granted ?? 0;
    summaryAgg.user_participation += s?.user_participation ?? 0;

    // Spend over time aggregation (existing)
    if (spend?.labels?.length) {
      labels = spend.labels;
    }
    for (const ds of spend?.datasets || []) {
      const existing = divisionMap.get(ds.division_name);
      if (existing) {
        const gLen = Math.max(existing.granted_values_minor.length, ds.granted_values_minor?.length ?? 0);
        existing.granted_values_minor = Array.from(
          { length: gLen },
          (_, i) => (existing.granted_values_minor[i] ?? 0) + (ds.granted_values_minor?.[i] ?? 0),
        );
      } else {
        divisionMap.set(ds.division_name, {
          division_name: ds.division_name,
          granted_values_minor: [...(ds.granted_values_minor || [])],
        });
      }
    }

    // Redemption aggregation (existing)
    redemptionTotals.cash += r?.cash?.amount_minor ?? 0;
    redemptionTotals.savings += r?.savings?.amount_minor ?? 0;
    redemptionTotals.gift_cards += r?.gift_cards?.amount_minor ?? 0;

    // Engagement aggregation
    if (eng) {
      engagementCount += 1;
      engagementAgg.recognition_sent += eng.recognition_sent ?? 0;
      engagementAgg.recognition_sent_change_pct += eng.recognition_sent_change_pct ?? 0;
      engagementAgg.nominations_received += eng.nominations_received ?? 0;
      engagementAgg.nominations_received_change_pct += eng.nominations_received_change_pct ?? 0;
      engagementAgg.employee_participation += eng.employee_participation ?? 0;
      engagementAgg.employee_participation_change_pct += eng.employee_participation_change_pct ?? 0;
      // Aggregate trend arrays point-by-point
      const sentLen = Math.max(engagementAgg.recognition_sent_trend.length, eng.recognition_sent_trend?.length ?? 0);
      engagementAgg.recognition_sent_trend = Array.from(
        { length: sentLen },
        (_, i) => (engagementAgg.recognition_sent_trend[i] ?? 0) + (eng.recognition_sent_trend?.[i] ?? 0),
      );
      const nomLen = Math.max(
        engagementAgg.nominations_received_trend.length,
        eng.nominations_received_trend?.length ?? 0,
      );
      engagementAgg.nominations_received_trend = Array.from(
        { length: nomLen },
        (_, i) => (engagementAgg.nominations_received_trend[i] ?? 0) + (eng.nominations_received_trend?.[i] ?? 0),
      );
      const partLen = Math.max(
        engagementAgg.employee_participation_trend.length,
        eng.employee_participation_trend?.length ?? 0,
      );
      engagementAgg.employee_participation_trend = Array.from(
        { length: partLen },
        (_, i) => (engagementAgg.employee_participation_trend[i] ?? 0) + (eng.employee_participation_trend?.[i] ?? 0),
      );
    }

    // Top employees aggregation
    const topEmpArray = Array.isArray(topEmp) ? topEmp : [];
    for (const emp of topEmpArray) {
      const existing = employeeMap.get(emp.employee_id);
      if (existing) {
        existing.nominations_count += emp.nominations_count ?? 0;
        existing.recognitions_count += emp.recognitions_count ?? 0;
      } else {
        employeeMap.set(emp.employee_id, { ...emp });
      }
    }

    // Top recognisers aggregation
    const topRecArray = Array.isArray(topRec) ? topRec : [];
    for (const rec of topRecArray) {
      const existing = recogniserMap.get(rec.employee_id);
      if (existing) {
        existing.sent_count += rec.sent_count ?? 0;
      } else {
        recogniserMap.set(rec.employee_id, { ...rec });
      }
    }

    // Qualities aggregation
    const qualArray = Array.isArray(qual?.qualities) ? qual.qualities : [];
    for (const q of qualArray) {
      const existing = qualityMap.get(q.quality_id);
      if (existing) {
        existing.count += q.count ?? 0;
        // Merge top_employees from this program into the existing quality
        for (const emp of q.top_employees ?? []) {
          const existingEmp = existing.top_employees?.find(e => e.employee_id === emp.employee_id);
          if (existingEmp) {
            existingEmp.recognitions_count += emp.recognitions_count ?? 0;
          } else {
            existing.top_employees = existing.top_employees ?? [];
            existing.top_employees.push({ ...emp });
          }
        }
      } else {
        qualityMap.set(q.quality_id, {
          ...q,
          top_employees: (q.top_employees ?? []).map(e => ({ ...e })),
        });
      }
    }
  }

  const count = results.length || 1;
  summaryAgg.user_participation = Math.round(summaryAgg.user_participation / count);

  // Average engagement change percentages and participation (only by programs that returned data)
  const engagementDivisor = engagementCount || 1;
  engagementAgg.recognition_sent_change_pct = Math.round(engagementAgg.recognition_sent_change_pct / engagementDivisor);
  engagementAgg.nominations_received_change_pct = Math.round(
    engagementAgg.nominations_received_change_pct / engagementDivisor,
  );
  engagementAgg.employee_participation = Math.round(engagementAgg.employee_participation / engagementDivisor);
  engagementAgg.employee_participation_change_pct = Math.round(
    engagementAgg.employee_participation_change_pct / engagementDivisor,
  );
  // Average participation trend (it's a percentage, not a count)
  engagementAgg.employee_participation_trend = engagementAgg.employee_participation_trend.map(v =>
    Math.round(v / engagementDivisor),
  );

  const redemptionTotal = redemptionTotals.cash + redemptionTotals.savings + redemptionTotals.gift_cards;
  const pct = v => (redemptionTotal > 0 ? Math.round((v / redemptionTotal) * 100) : 0);

  // Sort and limit top employees/recognisers
  const sortedEmployees = [...employeeMap.values()]
    .sort((a, b) => b.recognitions_count - a.recognitions_count)
    .slice(0, 5);
  const sortedRecognisers = [...recogniserMap.values()].sort((a, b) => b.sent_count - a.sent_count).slice(0, 5);

  // Calculate quality percentages and sort merged top_employees
  const qualitiesArray = [...qualityMap.values()];
  const totalQualityCount = qualitiesArray.reduce((sum, q) => sum + q.count, 0);
  for (const q of qualitiesArray) {
    q.percentage = totalQualityCount > 0 ? Math.round((q.count / totalQualityCount) * 100) : 0;
    if (q.top_employees?.length) {
      q.top_employees.sort((a, b) => b.recognitions_count - a.recognitions_count);
      q.top_employees = q.top_employees.slice(0, 5);
    }
  }

  return {
    summary: summaryAgg,
    spend: { labels, datasets: [...divisionMap.values()] },
    redemption: {
      cash: { amount_minor: redemptionTotals.cash, percentage: pct(redemptionTotals.cash) },
      savings: { amount_minor: redemptionTotals.savings, percentage: pct(redemptionTotals.savings) },
      gift_cards: { amount_minor: redemptionTotals.gift_cards, percentage: pct(redemptionTotals.gift_cards) },
    },
    engagement: engagementAgg,
    topEmployees: sortedEmployees,
    topRecognisers: sortedRecognisers,
    qualities: { qualities: qualitiesArray },
  };
};

const fetchAnalytics = async () => {
  const programIds = activeProgramId.value
    ? [activeProgramId.value]
    : props.programs.map(p => p.recognition_award_program_id);

  if (programIds.length === 0) {
    return;
  }

  fetchRequestId.value += 1;
  const currentRequestId = fetchRequestId.value;

  loading.value = true;
  error.value = false;
  summary.value = undefined;
  spendOverTime.value = null;
  redemption.value = undefined;
  engagement.value = undefined;
  topEmployees.value = [];
  topRecognisers.value = [];
  qualities.value = undefined;

  try {
    const params = buildParams();
    const results = await Promise.all(
      programIds.map(id =>
        Promise.all([
          ApiClient.getRecognitionAnalyticsSummary(id, params),
          ApiClient.getRecognitionAnalyticsSpendOverTime(id, params),
          ApiClient.getRecognitionAnalyticsRedemptionBreakdown(id, params),
          ApiClient.getRecognitionAnalyticsEngagement(id, params).catch(() => null),
          ApiClient.getRecognitionAnalyticsTopRecognised(id, params).catch(() => null),
          ApiClient.getRecognitionAnalyticsTopRecognisers(id, params).catch(() => null),
          ApiClient.getRecognitionAnalyticsCompanyValues(id, params).catch(() => null),
        ]).then(async ([s, spend, r, eng, topEmp, topRec, qual]) => {
          const transformedQual = transformQualities(qual);

          if (transformedQual?.qualities?.length) {
            const distributions = await Promise.all(
              transformedQual.qualities.map(q =>
                ApiClient.getRecognitionAnalyticsValueDistribution(id, q.quality_name, params).catch(() => null),
              ),
            );
            for (let i = 0; i < transformedQual.qualities.length; i++) {
              transformedQual.qualities[i].top_employees = distributions[i]?.top_employees ?? [];
            }
          }

          return [
            transformSummary(s),
            transformSpendOverTime(spend),
            transformRedemptionBreakdown(r),
            transformEngagement(eng),
            transformTopEmployees(topEmp),
            transformTopRecognisers(topRec),
            transformedQual,
          ];
        }),
      ),
    );

    if (currentRequestId !== fetchRequestId.value) {
      return;
    }

    const aggregated = aggregateResults(results);
    summary.value = aggregated.summary;
    spendOverTime.value = aggregated.spend;
    redemption.value = aggregated.redemption;
    engagement.value = aggregated.engagement;
    topEmployees.value = aggregated.topEmployees;
    topRecognisers.value = aggregated.topRecognisers;
    qualities.value = aggregated.qualities;
  } catch (e) {
    console.error('Analytics fetch error:', e);
    if (currentRequestId !== fetchRequestId.value) {
      return;
    }
    summary.value = null;
    spendOverTime.value = null;
    redemption.value = null;
    engagement.value = null;
    topEmployees.value = [];
    topRecognisers.value = [];
    qualities.value = null;
    error.value = true;
  } finally {
    if (currentRequestId === fetchRequestId.value) {
      loading.value = false;
    }
  }
};

const resetFilters = () => {
  selectedDivision.value = null;
  selectedProgram.value = null;
  selectedDateRange.value = 'last_12_months';
};

// Watch filters and refetch
watch([selectedDivision, activeProgramId, selectedDateRange], () => {
  fetchAnalytics();
});

onMounted(() => {
  fetchAnalytics();
});
</script>

<style scoped lang="scss">
.analytics-tab {
  &__tables-row {
    margin-bottom: 1.5rem;

    --bs-gutter-x: 1rem;
    --bs-gutter-y: 1rem;
  }
}
</style>
